import { appConfig } from '@/config/app-config';
// profile/index.ts
import { Module, MutationTree, GetterTree } from 'vuex';
import { AuthState } from '@auth/interfaces/auth-state';
import { RootState } from '@core/interfaces/root-state';
import { ActionTree } from 'vuex';
import BPCMessageService from '@core/services/bpc-messages.service';
import { Login, ApiLoginResponse } from '@auth/interfaces/login';
import router from '@app/router/index';
import { User } from '@auth/interfaces/user';
import { get } from 'lodash';
import store from '@app/store';
import { Center } from '@core/interfaces/tenant-state';
import AuthorizationService from '@auth/services/authorization.service';
import BPCApiRouteHelper from '@core/helpers/bpc-api-route.helper';
import { bpcApi } from '@core/services/bpc-api.service';
import { Profile } from '@core/interfaces/profile';
import { getProp, setProp } from '@shared/helpers/operators';
import AuthService from '@auth/services/auth.service';

const bpcRouterHelper = new BPCApiRouteHelper();

const bpcMessage = new BPCMessageService();

const authService = new AuthService();

// Initial state
const state: AuthState = {
  loading: false,
  authToken: localStorage.getItem('auth_token'),
  refreshToken: localStorage.getItem('refresh_token'),
  user: JSON.parse(localStorage.getItem('user')),
  isSuperAdmin: localStorage.getItem('is_super_admin') === 'true',
  showHiddenMenuItems: localStorage.getItem('show_hidden_menu_items') === 'true',
  lang: localStorage.getItem('lang'),
  resetRecaptcha: false,
};

// Synchronous functions to get data from state (can be composed properties)
const getters: GetterTree<AuthState, RootState> = {
  authToken(state): string {
    return state.authToken;
  },
  refreshToken(state): string {
    return state.refreshToken;
  },
  isLoading(state): boolean {
    return !!state.loading;
  },
  isLoggedIn(state): boolean {
    return !!state.authToken;
  },
  userName(state): string {
    return state.user ? state.user.name : null;
  },
  user(state): User {
    return state.user;
  },
  userLang(state): string {
    return state.lang;
  },
  isSuperAdmin(state): boolean {
    return !!state.isSuperAdmin;
  },
  showHiddenMenuItems(state): boolean {
    return !!state.showHiddenMenuItems;
  },
  permissions: state => payload => {
    let permissions;
    if (state.user && state.user.profiles) {
      const profile = state.user.profiles.find(profile => {
        return profile['center_id'] == payload.tenantId;
      });
      if (profile) {
        permissions = profile.roles[0].permissions;
      }
    }
    return permissions;
  },
  roles: state => payload => {
    let permissions;
    if (state.user && state.user.profiles) {
      const profile = state.user.profiles.find(profile => {
        return profile['center_id'] == payload.tenantId;
      });
      if (profile) {
        permissions = profile.roles;
      }
    }
    return permissions;
  },
  mainRole: state => payload => {
    let role;
    if (state.user && state.user.profiles) {
      const profile = state.user.profiles.find(profile => {
        return profile['center_id'] == payload.tenantId;
      });
      role = profile?.roles.find(role => {
        return role['main'];
      });
    }
    return role ? role.name : role;
  },
  contactId: state => payload => {
    let contactId;
    if (state.user && state.user.profiles) {
      const profile = state.user.profiles.find(profile => {
        return profile['center_id'] == payload.tenantId;
      });
      if (profile) {
        contactId = profile.roles[0].contact_id;
      }
    }
    return contactId;
  },
  resetRecaptcha(state): boolean {
    return !!state.resetRecaptcha;
  },
  profile: state => payload => {
    if (state.user && state.user.profiles) {
      return state.user.profiles.find(profile => {
        return profile['center_id'] == payload.tenantId;
      });
    }
    return null;
  },
};

// Asynchornous functions to get data usually from an api call
const actions: ActionTree<AuthState, RootState> = {
  // Login method
  login(
    { commit, dispatch },
    payload: {
      formModel: Login;
      tenantAlias: string;
      redirect: boolean;
    },
  ): Promise<boolean> {
    return new Promise((resolve, reject) => {
      bpcApi.post(bpcRouterHelper.getLoginPath(), payload.formModel, {}, 'public').subscribe(
        (res: ApiLoginResponse) => {
          const data = {
            authToken: res.data.access_token,
            refreshToken: res.data.refresh_token,
            isSuperAdmin: res.data.super_admin,
            centers: res.data.centers,
            tenantId: res.data.centers[0].id,
            tenantAlias: res.data.centers[0].alias,
            user: res.data.user,
            redirect: payload.redirect,
          };
          if (
            !payload.formModel.tenantAlias ||
            payload.formModel.tenantAlias === appConfig.globalTenantAlias
          ) {
            commit('globalLoggedIn', data);
          } else {
            commit('loggedIn', data);
          }
          resolve(true);
        },
        error => {
          commit('restartRecaptcha');
          if (error.response) {
            if (error.response.status == '400') {
              bpcMessage.showErrorMessage({ text: 'global.auth.invalid_access_data' });
            } else if (error.response.status == '403') {
              bpcMessage.showErrorMessage({ text: error.response.data.message });
            } else if (error.response.status == '422') {
              // This logic only is for this form that doesn't use std-form. The only rule that exists is in field email that must has email format
              if (error.response.data.errors) {
                const emailError = get(error, 'response.data.errors.email[0]');
                if (emailError != null) {
                  bpcMessage.showErrorMessage({ text: emailError });
                }
              }
            }
          }
          commit('stopLoading');
          reject(error);
        },
      );
    });
  },

  updateProfileLanguage(
    { commit },
    payload: { profileId: number; language: string },
  ): Promise<boolean> {
    return new Promise((resolve, reject) => {
      bpcApi
        .patch(
          `${bpcRouterHelper.getEntityPath('profile')}/${payload.profileId}/language`,
          payload,
          {},
          'admin',
        )
        .subscribe(
          res => {
            store.commit('auth/updateProfile', res.data);
            resolve(true);
          },
          error => {
            reject(error);
          },
        );
    });
  },

  async refreshLogin(
    { commit, getters },
    payload: { refresh_token: string; tenantAlias: string; redirect: boolean },
  ) {
    return new Promise((resolve, reject) => {
      if (!getters.isLoading) {
        commit('logging');
        bpcApi.post(bpcRouterHelper.getRefreshTokenPath(), payload).subscribe(
          (res: ApiLoginResponse) => {
            const data = {
              authToken: res.data.access_token,
              refreshToken: res.data.refresh_token,
              isSuperAdmin: res.data.super_admin,
              centers: res.data.centers,
              tenantId: res.data.centers[0].id,
              tenantAlias: res.data.centers[0].alias,
              user: res.data.user,
              redirect: payload.redirect,
            };
            commit('loggedIn', data);
            resolve(true);
          },
          error => {
            if (error.response && error.response.status === '401') {
              // If the refresh token isn't valid we redirect to login page
              commit('logout');
              router.push({ name: 'auth-login' });
            }
            commit('stopLoading');
            reject(error);
          },
        );
      }
    });
  },

  // Simple logout
  logout({ commit }): void {
    const selectedTenant = store.getters['tenant/selectedTenant'];
    const alias = selectedTenant?.alias;

    commit('logout');
    if (alias) {
      router.push({ name: 'auth-tenant-login', params: { tenantAlias: alias } });
    } else {
      router.push({ name: 'auth-tenant-login' });
    }
  },

  getProfilesAndTenantsAfterLogin({ commit }): Promise<boolean> {
    return new Promise((resolve, reject) => {
      bpcApi.get(bpcRouterHelper.getMyTenantsAndProfilesPath(), {}, 'admin').subscribe(
        res => {
          store.commit('tenant/storeTenants', res.data['tenants']);
          store.commit('auth/storeProfiles', res.data['profiles']);
          resolve(true);
        },
        error => {
          reject(error);
        },
      );
    });
  },
};

// The vuex way of changing a state property
export const mutations: MutationTree<AuthState> = {
  stopLoading(state) {
    state.loading = false;
  },
  logging(state) {
    state.loading = true;
  },

  globalLoggedIn(
    state,
    data: {
      refreshToken: string;
      tenantAlias: string;
    },
  ) {
    state.loading = false;
    authService.navigateToTenantWithAuthByQuery(data.tenantAlias, data.refreshToken);
  },

  async loggedIn(
    state,
    data: {
      authToken: string;
      refreshToken?: string;
      isSuperAdmin: boolean;
      centers: Center[];
      tenantId: number;
      user: User;
      redirect: boolean;
    },
  ) {
    state.loading = false;
    state.authToken = data.authToken;
    state.refreshToken = data.refreshToken;
    state.isSuperAdmin = data.isSuperAdmin;

    const selectedTenant = data.centers[0];

    const previousLanguage = store.getters['auth/userLang'];
    const language =
      data.user.profiles[0]?.language || selectedTenant.center_setting.default_language;
    const availableLanguages = selectedTenant.center_setting.available_languages;
    store.commit('auth/setUserLang', language);
    store.commit('tenant/storeTenants', data.centers);
    store.commit('tenant/selectTenant', selectedTenant);
    localStorage.setItem('auth_token', data.authToken);
    localStorage.setItem('refresh_token', data.refreshToken);
    localStorage.setItem('is_super_admin', String(data.isSuperAdmin));
    localStorage.setItem('show_hidden_menu_items', 'false');
    store.commit('centerSettings/updateCenterSettings', selectedTenant.center_setting);

    const userData = {
      id: data.user.id,
      name: data.user.name,
      email: data.user.email,
      avatar: data.user.avatar,
      profiles: data.user.profiles,
    };

    store.commit('auth/storeUser', userData);

    // Dispatch the call to get all the centers for the current profile
    store.dispatch('auth/getProfilesAndTenantsAfterLogin');
    try {
      // Dispatch the call to get access control config
      await store.dispatch('centerSettings/getAccessConfigAfterLogin');
    } catch (error) {
      console.error('Error in sequential dispatch: ', error);
    }

    const $authorizationService = new AuthorizationService();
    const redirectAfterLogin = router.currentRoute.query.redirect;

    if (redirectAfterLogin) {
      if (previousLanguage !== language || !availableLanguages.includes(language)) {
        window.location.href = redirectAfterLogin.toString();
      } else {
        router.push(redirectAfterLogin.toString());
      }
    } else if (data.redirect) {
      if (previousLanguage !== language || !availableLanguages.includes(language)) {
        const routeObject = $authorizationService.getRedirectToHomePage();
        window.location.href = router.resolve(routeObject).href;
      } else {
        router.push($authorizationService.getRedirectToHomePage());
      }
    }
  },

  loggingOut(state) {
    state.loading = true;
  },

  logout(state) {
    state.loading = false;
    state.authToken = null;
    state.refreshToken = null;
    state.isSuperAdmin = null;

    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('is_super_admin');
    localStorage.removeItem('show_hidden_menu_items');
    localStorage.removeItem('access_control_config');
  },

  clearToken(state) {
    state.authToken = null;
    localStorage.removeItem('auth_token');
  },

  setToken(state, token) {
    localStorage.setItem('auth_token', token);
    state.authToken = token;
  },

  clearRefreshToken(state) {
    state.refreshToken = null;
    localStorage.removeItem('refresh_token');
  },

  setRefreshToken(state, refreshToken) {
    localStorage.setItem('refresh_token', refreshToken);
    state.refreshToken = refreshToken;
  },

  setUserLang(state, lang: string) {
    localStorage.setItem('lang', lang);
    state.lang = lang;
  },

  storeUser(state, data: User) {
    localStorage.setItem('user', JSON.stringify(data));
    state.user = data;
  },

  setIsSuperAdmin(state, isSuperAdmin: boolean) {
    state.isSuperAdmin = isSuperAdmin;
    localStorage.setItem('is_super_admin', String(isSuperAdmin));
  },

  clearIsSuperAdmin(state) {
    state.isSuperAdmin = null;
    localStorage.removeItem('is_super_admin');
  },
  restartRecaptcha(state) {
    state.resetRecaptcha = true;
  },

  restartedRecaptcha(state) {
    state.resetRecaptcha = false;
  },

  storeProfiles(state, data: Profile[]) {
    const user = store.getters['auth/user'];
    setProp(user, 'profiles', data);
    store.commit('auth/storeUser', user);
  },
  updateProfile(state, data: Profile) {
    const user = store.getters['auth/user'];
    const index = user.profiles.findIndex(profile => profile.center_id === data.center_id);
    const profiles = getProp(user, 'profiles');
    profiles[index] = data;
    setProp(user, 'profiles', profiles);
    store.commit('auth/storeUser', user);
  },
};

export const AuthStoreModule: Module<AuthState, RootState> = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
